import { useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../../stores/authStore'
import api from '../../lib/api'
import type { TutorialProgress, TutorialProgressStatus } from '../types'

const QUERY_KEY = 'tutorial-progress'
const STORAGE_PREFIX = 'smartdental_tutorial_progress_'

function storageKey(userId: string) {
  return `${STORAGE_PREFIX}${userId}`
}

function loadLocalProgress(userId: string): Record<string, TutorialProgress> {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) return {}
    return JSON.parse(raw) as Record<string, TutorialProgress>
  } catch {
    return {}
  }
}

function saveLocalProgress(userId: string, progress: Record<string, TutorialProgress>) {
  localStorage.setItem(storageKey(userId), JSON.stringify(progress))
}

export function useTutorialProgress() {
  const { user } = useAuthStore()
  const userId = user?.id
  const queryClient = useQueryClient()

  // Cargar progreso del backend
  const { data: serverProgress } = useQuery({
    queryKey: [QUERY_KEY, userId],
    queryFn: async () => {
      const { data } = await api.get('/tutorials/progress')
      return (data.progress as TutorialProgress[]) ?? []
    },
    enabled: !!userId,
    staleTime: 60 * 1000,
  })

  // Merge: servidor tiene prioridad, localStorage como fallback
  const progressMap: Record<string, TutorialProgress> = {}

  // Primero cargar localStorage
  const localProgress = userId ? loadLocalProgress(userId) : {}
  Object.assign(progressMap, localProgress)

  // Luego sobreescribir con servidor (tiene prioridad)
  if (serverProgress) {
    for (const p of serverProgress) {
      progressMap[p.tutorialId] = p
    }
  }

  // Sincronizar localStorage con servidor cuando cambia
  useEffect(() => {
    if (serverProgress && userId) {
      saveLocalProgress(userId, progressMap)
    }
  }, [serverProgress, userId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Mutations
  const markCompletedMutation = useMutation({
    mutationFn: async (tutorialId: string) => {
      const { data } = await api.post(`/tutorials/${tutorialId}/complete`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, userId] })
    },
  })

  const markInProgressMutation = useMutation({
    mutationFn: async (tutorialId: string) => {
      const { data } = await api.post(`/tutorials/${tutorialId}/progress`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, userId] })
    },
  })

  const rateMutation = useMutation({
    mutationFn: async ({ tutorialId, rating, feedback }: { tutorialId: string; rating: number; feedback?: string }) => {
      const { data } = await api.post(`/tutorials/${tutorialId}/rate`, { rating, feedback })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, userId] })
    },
  })

  const getProgress = useCallback(
    (tutorialId: string): TutorialProgressStatus => {
      return progressMap[tutorialId]?.status ?? 'not_started'
    },
    [progressMap]
  )

  const markInProgress = useCallback(
    (tutorialId: string) => {
      if (!userId) return
      // Optimistic local update
      const next = { ...progressMap }
      next[tutorialId] = {
        ...next[tutorialId],
        userId,
        tutorialId,
        status: 'in_progress',
        lastAccessedAt: new Date().toISOString(),
      }
      saveLocalProgress(userId, next)
      queryClient.setQueryData([QUERY_KEY, userId], (old: TutorialProgress[] | undefined) => {
        const existing = old?.find((p) => p.tutorialId === tutorialId)
        if (existing) {
          return old?.map((p) => (p.tutorialId === tutorialId ? { ...p, status: 'in_progress' as const, lastAccessedAt: next[tutorialId].lastAccessedAt } : p))
        }
        return [...(old ?? []), next[tutorialId]]
      })
      // Server sync
      markInProgressMutation.mutate(tutorialId)
    },
    [userId, progressMap, queryClient, markInProgressMutation]
  )

  const markCompleted = useCallback(
    (tutorialId: string) => {
      if (!userId) return
      const now = new Date().toISOString()
      const next = { ...progressMap }
      next[tutorialId] = {
        userId,
        tutorialId,
        status: 'completed',
        completedAt: now,
        lastAccessedAt: now,
      }
      saveLocalProgress(userId, next)
      queryClient.setQueryData([QUERY_KEY, userId], (old: TutorialProgress[] | undefined) => {
        const existing = old?.find((p) => p.tutorialId === tutorialId)
        if (existing) {
          return old?.map((p) => (p.tutorialId === tutorialId ? { ...p, status: 'completed' as const, completedAt: now, lastAccessedAt: now } : p))
        }
        return [...(old ?? []), next[tutorialId]]
      })
      markCompletedMutation.mutate(tutorialId)
    },
    [userId, progressMap, queryClient, markCompletedMutation]
  )

  const rateTutorial = useCallback(
    (tutorialId: string, rating: number, feedback?: string) => {
      if (!userId) return
      const next = { ...progressMap }
      if (next[tutorialId]) {
        next[tutorialId] = { ...next[tutorialId], rating, feedback }
        saveLocalProgress(userId, next)
      }
      queryClient.setQueryData([QUERY_KEY, userId], (old: TutorialProgress[] | undefined) => {
        return old?.map((p) => (p.tutorialId === tutorialId ? { ...p, rating, feedback } : p))
      })
      rateMutation.mutate({ tutorialId, rating, feedback })
    },
    [userId, progressMap, queryClient, rateMutation]
  )

  const getCompletedCount = useCallback(() => {
    return Object.values(progressMap).filter((p) => p.status === 'completed').length
  }, [progressMap])

  return {
    progressMap,
    getProgress,
    markInProgress,
    markCompleted,
    rateTutorial,
    getCompletedCount,
  }
}
